import React from "react";
import { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  useWindowDimensions,
  Image,
} from "react-native";
import simulationService from "../../../../services/simulation";
import { useContext } from "react";
import { SessionContext } from "../../../../contexts";
import VoiceRecorder from "./VoiceRecorder";
import AudioUploader from "./AudioUploader";
import { mixpanel } from "../../../../utils/mixpanel";

type VoiceNoteResult = {
  transcription: string;
  summary: string;
  audioUrl: string;
  audioId: number;
  noteType: string;
};

type VoiceRecorderSession = {
  userEmail?: string;
  clinicName?: string;
  accessToken?: string;
};

const hasDisplayValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return false;
    const normalizedValue = trimmedValue.toLowerCase();
    if (normalizedValue === "n/a" || normalizedValue === "na") return false;
  }
  return true;
};

const getFirstDisplayValue = (...values: unknown[]): unknown =>
  values.find((value) => hasDisplayValue(value));

type PatientMetadata = {
  age?: string | number;
  height?: string | number;
  weight?: string | number;
  [key: string]: unknown;
};

type PatientDetail = {
  id: string;
  name: string;
  clinicOSUUID?: string;
  patientRecordId?: string;
  appointmentId?: number;
  appointmentTime?: string;
  procedureType?: string;
  email?: string;
  phone?: string;
  height?: string | number;
  weight?: string | number;
  abbreviatedProcedures?: (string | null | undefined)[];
  metadata?: PatientMetadata;
  autoStartRecording?: boolean;
  profileImageUrl?: string | null;
};

interface PatientDetailCardProps {
  patient: PatientDetail;
  onVoiceNoteSaved?: (result: VoiceNoteResult) => void;
  tipsEnabled?: boolean;
  autoStartRecording?: boolean;
  onAutoStartHandled?: () => void;
}

/**
 * Component to display detailed patient information in the planning view
 */
const PatientDetailCard: React.FC<PatientDetailCardProps> = ({
  patient,
  onVoiceNoteSaved,
  tipsEnabled = false,
  autoStartRecording = false,
  onAutoStartHandled,
}) => {
  const { width } = useWindowDimensions();
  const isWideLayout = width >= 1100;
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;
  const clinicId = session?.clinicId ?? "";
  const voiceRecorderSession: VoiceRecorderSession | undefined = session
    ? {
        userEmail: session.userEmail || undefined,
        clinicName: session.clinicName || undefined,
        accessToken: session.accessToken,
      }
    : undefined;

  const [supportedProcedures, setSupportedProcedures] = useState<string[]>([]);
  const [unsupportedProcedures, setUnsupportedProcedures] = useState<string[]>(
    [],
  );

  // Filter procedures on component mount
  useEffect(() => {
    try {
      if (
        patient &&
        patient.abbreviatedProcedures &&
        patient.abbreviatedProcedures.length > 0
      ) {
        const supported: string[] = [];
        const unsupported: string[] = [];

        // Process each procedure string
        patient.abbreviatedProcedures.forEach((procedureString) => {
          if (!procedureString) return;

          try {
            // Split by commas
            const parts = procedureString
              .split(",")
              .map((part) => part.trim())
              .filter(Boolean);

            // Check each part against our supported list
            let foundSupported = false;

            for (const part of parts) {
              try {
                if (simulationService.isProcedureSupported(part)) {
                  supported.push(part);
                  foundSupported = true;
                }
              } catch (partError) {
                console.error(`Error checking part "${part}":`, partError);
              }
            }

            // If no parts were supported, add the original string to unsupported
            if (!foundSupported) {
              unsupported.push(procedureString);
            }
          } catch (procError) {
            console.error(
              `Error processing procedure "${procedureString}":`,
              procError,
            );
            // Add to unsupported if there's an error
            unsupported.push(procedureString);
          }
        });

        // Remove duplicates
        setSupportedProcedures([...new Set(supported)]);
        setUnsupportedProcedures([...new Set(unsupported)]);
      } else {
        setSupportedProcedures([]);
        setUnsupportedProcedures([]);
      }
    } catch (error) {
      console.error("Error in PatientDetailCard procedure filtering:", error);
      // Set empty arrays to avoid UI errors
      setSupportedProcedures([]);
      setUnsupportedProcedures([]);
    }
  }, [patient]);

  // Handle voice note saved - matches the pattern from HomeConsults
  const handleVoiceNoteSaved = (result: VoiceNoteResult) => {
    // Track voice note saved
    mixpanel.track("Voice Note Saved", {
      "User Email": session?.userEmail,
      "Clinic Name": session?.clinicName,
      "Patient ID": patient?.clinicOSUUID,
      "Patient Name": patient?.name,
      "Audio ID": result.audioId,
      "Note Type": result.noteType,
    });

    // Notify parent component to refresh VoiceNotesList
    if (onVoiceNoteSaved) {
      onVoiceNoteSaved(result);
    }
  };

  const additionalDetails = [
    { label: "Age", value: patient.metadata?.age },
    {
      label: "Height",
      value: getFirstDisplayValue(patient.height, patient.metadata?.height),
    },
    {
      label: "Weight",
      value: getFirstDisplayValue(patient.weight, patient.metadata?.weight),
    },
  ].filter((detail) => hasDisplayValue(detail.value));
  const contactDetails = [
    { label: "Phone", value: patient.phone },
    { label: "Email", value: patient.email },
  ].filter((detail) => hasDisplayValue(detail.value));
  const patientDetails = [...contactDetails, ...additionalDetails];
  const avatarInitial = (patient.name?.trim()?.charAt(0) || "?").toUpperCase();

  return (
    <View style={styles.container} data-testid="patient-detail-card">
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Patient Information</Text>
      </View>

      <View style={styles.content}>
        <View
          style={[
            styles.detailsLayout,
            isWideLayout && styles.detailsLayoutWide,
          ]}
        >
          <View
            style={[
              styles.primaryColumn,
              isWideLayout && styles.primaryColumnWide,
            ]}
          >
            <View style={styles.patientInfoRow}>
              <View style={styles.avatarContainer}>
                {patient.profileImageUrl ? (
                  <Image
                    source={{ uri: patient.profileImageUrl }}
                    style={styles.avatarImage}
                  />
                ) : (
                  <Text style={styles.avatarText}>{avatarInitial}</Text>
                )}
              </View>
              <View style={styles.patientInfo}>
                <Text style={styles.patientName}>{patient.name}</Text>
                <Text style={styles.appointmentInfo}>
                  {patient.procedureType} - {patient.appointmentTime}
                </Text>

                {/* Add voice recorder component using HomeConsults pattern */}
                <VoiceRecorder
                  patient={patient}
                  clinicId={clinicId}
                  onSaveRecording={handleVoiceNoteSaved}
                  appointmentId={patient.appointmentId}
                  session={voiceRecorderSession}
                  tipsEnabled={tipsEnabled}
                  autoStart={autoStartRecording}
                  onAutoStartHandled={onAutoStartHandled}
                />

                <AudioUploader
                  patient={patient}
                  onUploadComplete={handleVoiceNoteSaved} // Use the same handler
                />
              </View>
            </View>
          </View>

          <View
            style={[
              styles.secondaryColumn,
              isWideLayout && styles.secondaryColumnWide,
            ]}
          >
            {patientDetails.length > 0 ? (
              <View
                style={[
                  styles.section,
                  isWideLayout && styles.secondaryFirstSectionWide,
                ]}
              >
                {patientDetails.map((detail) => (
                  <Text key={detail.label} style={styles.contactText}>
                    {detail.label}: {String(detail.value)}
                  </Text>
                ))}
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#EEEEEE",
    marginBottom: 12,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EEEEEE",
    backgroundColor: "#F9F9F9",
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333333",
  },
  content: {
    padding: 16,
  },
  detailsLayout: {
    flexDirection: "column",
  },
  detailsLayoutWide: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  primaryColumn: {
    width: "100%",
  },
  primaryColumnWide: {
    flex: 2,
    paddingRight: 24,
  },
  secondaryColumn: {
    width: "100%",
  },
  secondaryColumnWide: {
    flex: 1,
    minWidth: 260,
  },
  patientInfoRow: {
    flexDirection: "row",
    marginBottom: 12,
    alignItems: "center",
  },
  cardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#EEEEEE",
  },
  patientName: {
    fontSize: 20,
    fontWeight: "600",
    color: "#333333",
    marginBottom: 4,
  },
  procedureType: {
    fontSize: 16,
    color: "#666666",
    marginBottom: 16,
  },
  avatarContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#0085FF",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  avatarText: {
    fontSize: 20,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    borderRadius: 24,
  },
  patientInfo: {
    flex: 1,
  },
  appointmentInfo: {
    fontSize: 14,
    color: "#666666",
  },
  section: {
    marginTop: 12,
  },
  secondaryFirstSectionWide: {
    marginTop: 0,
  },
  proceduresContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  procedureChip: {
    backgroundColor: "#E3F2FD",
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    marginBottom: 8,
  },
  procedureChipText: {
    fontSize: 14,
    color: "#0D47A1",
  },
  emptyText: {
    fontSize: 14,
    color: "#999999",
    fontStyle: "italic",
  },
  contactText: {
    fontSize: 14,
    color: "#555555",
    marginBottom: 4,
  },
  subsectionTitle: {
    fontSize: 13,
    fontWeight: "500",
    color: "#666666",
    marginBottom: 6,
  },
  unsupportedProcedureChip: {
    backgroundColor: "#F5F5F5",
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  unsupportedProcedureText: {
    fontSize: 14,
    color: "#999999",
  },
});

export default PatientDetailCard;
